/**
 * Content Bucket Form Component
 * 
 * Form for creating and editing content buckets with validation,
 * color picker, frequency settings, and industry template selection.
 * 
 * Features:
 * - Create and edit modes
 * - Color picker with preset colors
 * - Frequency selection with custom option
 * - Industry template dropdown
 * - Real-time validation
 * - Mobile-responsive design
 * 
 * @author AI Assistant
 * @created 2024
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  FormHelperText,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Palette as PaletteIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { useContentBuckets } from '../providers/ContentBucketsProvider';

const ColorPicker = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1)
}));

const ColorOption = styled(Box)(({ color, selected, theme }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: color,
  border: selected ? `3px solid ${theme.palette.primary.main}` : '2px solid #fff',
  boxShadow: theme.shadows[2],
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: theme.shadows[4]
  }
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 400,
    maxWidth: 600,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      maxWidth: '95vw',
      margin: theme.spacing(1)
    }
  }
}));

// Preset color palette
const COLOR_PALETTE = [
  '#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0',
  '#00BCD4', '#CDDC39', '#FF5722', '#607D8B', '#795548',
  '#E91E63', '#3F51B5', '#009688', '#FFC107', '#8BC34A',
  '#673AB7', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'
];

// Industry template options
const INDUSTRY_TEMPLATES = [
  { value: 'business_coach', label: 'Business Coach' },
  { value: 'fitness_creator', label: 'Fitness Creator' },
  { value: 'food_brand', label: 'Food Brand' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'tech_startup', label: 'Tech Startup' },
  { value: 'custom', label: 'Custom' }
];

// Frequency options
const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom' }
];

const ContentBucketForm = ({ 
  open, 
  onClose, 
  onSuccess, 
  bucket = null, 
  mode = 'create' 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { createBucket, updateBucket, loading, error, clearError } = useContentBuckets();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: COLOR_PALETTE[0],
    industry_template: 'custom',
    target_frequency: 'weekly',
    custom_frequency_days: '',
    is_active: true
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data when bucket changes
  useEffect(() => {
    if (bucket && mode === 'edit') {
      setFormData({
        name: bucket.name || '',
        description: bucket.description || '',
        color: bucket.color || COLOR_PALETTE[0],
        industry_template: bucket.industry_template || 'custom',
        target_frequency: bucket.target_frequency || 'weekly',
        custom_frequency_days: bucket.custom_frequency_days || '',
        is_active: bucket.is_active !== undefined ? bucket.is_active : true
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: COLOR_PALETTE[0],
        industry_template: 'custom',
        target_frequency: 'weekly',
        custom_frequency_days: '',
        is_active: true
      });
    }
    setErrors({});
    clearError();
  }, [bucket, mode, open, clearError]);

  // Validation rules
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Bucket name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Bucket name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Bucket name must be less than 100 characters';
    }

    // Description validation
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    // Custom frequency validation
    if (formData.target_frequency === 'custom') {
      if (!formData.custom_frequency_days) {
        newErrors.custom_frequency_days = 'Custom frequency is required';
      } else {
        const days = parseInt(formData.custom_frequency_days);
        if (isNaN(days) || days < 1 || days > 365) {
          newErrors.custom_frequency_days = 'Custom frequency must be between 1 and 365 days';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form field changes
  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear custom frequency when not needed
    if (field === 'target_frequency' && value !== 'custom') {
      setFormData(prev => ({
        ...prev,
        custom_frequency_days: ''
      }));
    }
  };

  // Handle color selection
  const handleColorSelect = (color) => {
    setFormData(prev => ({
      ...prev,
      color
    }));
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    clearError();

    try {
      const submitData = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim(),
        custom_frequency_days: formData.target_frequency === 'custom' 
          ? parseInt(formData.custom_frequency_days) 
          : null
      };

      if (mode === 'edit' && bucket) {
        await updateBucket(bucket.id, submitData);
      } else {
        await createBucket(submitData);
      }

      onSuccess?.();
    } catch (err) {
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="h2">
          {mode === 'edit' ? 'Edit Content Bucket' : 'Create Content Bucket'}
        </Typography>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Bucket Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bucket Name"
                value={formData.name}
                onChange={handleChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                placeholder="e.g., Behind the Scenes, Product Features"
                disabled={isSubmitting}
                required
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description (Optional)"
                value={formData.description}
                onChange={handleChange('description')}
                error={!!errors.description}
                helperText={errors.description || `${formData.description.length}/500 characters`}
                placeholder="Describe what type of content goes in this bucket..."
                disabled={isSubmitting}
              />
            </Grid>

            {/* Industry Template */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Industry Template</InputLabel>
                <Select
                  value={formData.industry_template}
                  onChange={handleChange('industry_template')}
                  label="Industry Template"
                  disabled={isSubmitting}
                >
                  {INDUSTRY_TEMPLATES.map(template => (
                    <MenuItem key={template.value} value={template.value}>
                      {template.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Target Frequency */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Target Frequency</InputLabel>
                <Select
                  value={formData.target_frequency}
                  onChange={handleChange('target_frequency')}
                  label="Target Frequency"
                  disabled={isSubmitting}
                >
                  {FREQUENCY_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Custom Frequency Days */}
            {formData.target_frequency === 'custom' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Custom Frequency (Days)"
                  value={formData.custom_frequency_days}
                  onChange={handleChange('custom_frequency_days')}
                  error={!!errors.custom_frequency_days}
                  helperText={errors.custom_frequency_days}
                  placeholder="e.g., 3"
                  disabled={isSubmitting}
                  inputProps={{ min: 1, max: 365 }}
                  required
                />
              </Grid>
            )}

            {/* Color Picker */}
            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  <PaletteIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} />
                  Bucket Color
                </Typography>
                <ColorPicker>
                  {COLOR_PALETTE.map(color => (
                    <ColorOption
                      key={color}
                      color={color}
                      selected={formData.color === color}
                      onClick={() => handleColorSelect(color)}
                    />
                  ))}
                </ColorPicker>
              </Box>
            </Grid>

            {/* Active Status */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleChange('is_active')}
                    disabled={isSubmitting}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      Active Bucket
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Inactive buckets won't appear in post assignment options
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {isSubmitting 
              ? (mode === 'edit' ? 'Updating...' : 'Creating...') 
              : (mode === 'edit' ? 'Update Bucket' : 'Create Bucket')
            }
          </Button>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default ContentBucketForm; 